import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr, downloadBlob } from "@/lib/api";
import { Button, IconButton, TextField, Panel, PageHead, StatCell, EmptyState } from "@/sentinel";

const money = (currency, v) => `${currency} ${(Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Row({ r, rate, currency, canEdit, refresh }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ description: r.description, hours: r.hours, date: r.date });
  const save = async () => {
    try { await api.pmUpdateTime(r.id, { ...f, hours: Number(f.hours) }); setEditing(false); refresh(); toast.success("Updated."); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const del = async () => { try { await api.pmDeleteTime(r.id); refresh(); } catch (e) { toast.error(e.message); } };

  if (editing) return (
    <tr data-testid={`pm-time-${r.id}`}>
      <td><TextField aria-label="Date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} style={{ width: 112 }} /></td>
      <td><TextField aria-label="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></td>
      <td>{r.who}</td>
      <td className="r"><TextField aria-label="Hours" type="number" step="0.25" numeric value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} style={{ width: 64 }} /></td>
      <td className="num">{money(currency, Number(f.hours) * rate)}</td>
      <td>
        <div className="sn-row" style={{ gap: "var(--space-1)", flexWrap: "nowrap" }}>
          <IconButton icon="Check" label="Save entry" size="sm" onClick={save} />
          <IconButton icon="X" label="Cancel editing" size="sm" onClick={() => setEditing(false)} />
        </div>
      </td>
    </tr>
  );
  return (
    <tr data-testid={`pm-time-${r.id}`}>
      <td className="sn-num" style={{ fontSize: 12 }}>{r.date}</td>
      <td style={{ color: "var(--text-primary)", whiteSpace: "normal" }}>{r.description}</td>
      <td>{r.who}</td>
      <td className="num">{r.hours}</td>
      <td className="num">{money(currency, r.hours * rate)}</td>
      <td>
        {canEdit && (
          <div className="sn-row" style={{ gap: "var(--space-1)", flexWrap: "nowrap" }}>
            <IconButton icon="Pencil" label="Edit entry" size="sm" onClick={() => setEditing(true)} />
            <IconButton icon={Trash2} label="Delete entry" size="sm" onClick={del} />
          </div>
        )}
      </td>
    </tr>
  );
}

export default function TimeLog() {
  const { can } = useAuth();
  const canEdit = can("analyst");
  const qc = useQueryClient();
  const [form, setForm] = useState({ description: "", hours: 1, who: "team", date: "" });
  const [rateEdit, setRateEdit] = useState("");
  const [savingRate, setSavingRate] = useState(false);
  const { data, refetch } = useQuery({ queryKey: ["pm-timelog"], queryFn: api.pmTimelog });
  const rows = data?.entries || [];
  const rate = data?.rate ?? 100;
  const currency = data?.currency ?? "AUD";

  useEffect(() => { if (data) setRateEdit(String(data.rate)); }, [data?.rate]);
  const refresh = () => { refetch(); qc.invalidateQueries({ queryKey: ["pm-summary"] }); };

  const saveRate = async () => {
    setSavingRate(true);
    try { await api.pmUpdateSettings({ labour_rate: Number(rateEdit), currency }); refresh(); toast.success("Rate updated."); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setSavingRate(false); }
  };
  const add = async () => {
    if (!form.description.trim()) { toast.error("Description required."); return; }
    try { await api.pmAddTime({ ...form, hours: Number(form.hours) }); setForm({ description: "", hours: 1, who: form.who, date: "" }); refresh(); toast.success("Time logged."); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="pm-timelog">
      <PageHead
        crumb={["Project", "Time log"]}
        title="Time log"
        context={<>Total <span className="sn-num">{data?.total_hours ?? 0} h</span> · labour cost <span className="sn-num">{currency} {data?.total_cost ?? 0}</span></>}
        actions={<Button icon="Download" onClick={async () => downloadBlob(await api.pmExport("timelog"), "sentinel_timelog.csv")} data-testid="pm-export-timelog">CSV</Button>}
      />

      <div className="sn-grid-12">
        <Panel className="sn-col-4" title="Project rate" icon="DollarSign" data-testid="pm-rate-control">
          <div className="sn-stack">
            <div className="sn-row" style={{ alignItems: "flex-end" }}>
              <TextField label={`Hourly rate (${currency}/h)`} type="number" step="1" numeric value={rateEdit} onChange={(e) => setRateEdit(e.target.value)}
                disabled={!canEdit} style={{ width: 112 }} data-testid="pm-rate-input" />
              {canEdit && <Button variant="primary" onClick={saveRate} loading={savingRate} data-testid="pm-rate-save">Save rate</Button>}
            </div>
            <span className="sn-help">Applies to all entries.</span>
          </div>
        </Panel>
        <Panel className="sn-col-8" title="Totals" icon="Timer">
          <div className="sn-row" style={{ gap: "var(--space-8)" }}>
            <StatCell size="md" label="Hours" value={String(data?.total_hours ?? 0)} unit="h" />
            <StatCell size="md" label="Labour cost" value={String(data?.total_cost ?? 0)} unit={currency} />
            <StatCell size="md" label="Entries" value={String(rows.length)} />
          </div>
        </Panel>
      </div>

      {canEdit && (
        <Panel title="Log time" icon="Plus" data-testid="pm-time-form">
          <div className="sn-row" style={{ alignItems: "flex-end", gap: "var(--space-3)" }}>
            <TextField label="What was done" placeholder="What was done" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="flex-1" style={{ minWidth: 220 }} data-testid="pm-time-desc" />
            <TextField label="Hours" type="number" step="0.25" numeric placeholder="Hours" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })}
              style={{ width: 80 }} data-testid="pm-time-hours" />
            <TextField label="Who" placeholder="Who" value={form.who} onChange={(e) => setForm({ ...form, who: e.target.value })} style={{ width: 112 }} />
            <Button variant="primary" icon="Plus" onClick={add} data-testid="pm-time-add">Log</Button>
          </div>
        </Panel>
      )}

      <Panel title="Entries" icon="ListOrdered" flush meta={<span className="sn-num">{rows.length}</span>}>
        {rows.length === 0 ? (
          <div style={{ padding: "var(--space-4)" }}><EmptyState icon="Timer" title="No time logged yet" /></div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="sn-table">
              <thead><tr><th>Date</th><th>Description</th><th>Who</th><th className="r">Hours</th><th className="r">Cost</th><th><span className="sn-vh">Actions</span></th></tr></thead>
              <tbody>
                {rows.map((r) => <Row key={r.id} r={r} rate={rate} currency={currency} canEdit={canEdit} refresh={refresh} />)}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
